import { useTranslation } from "react-i18next";
import {
  Col,
  ContentHeader,
  DesignTokens,
  FullWidthSpace,
  MainContentArea,
  Row,
  Space,
  Title,
  ValueOrFallback,
  LabeledItem,
  Text,
} from "melco-ui";
import { FormattedDate, HeaderButtonArea } from "melco-shared-logic";
import { useBreadcrumb } from "../../hooks/navigation/useBreadcrumb";
import { useLicense } from "../../hooks/infrastructure/useLicense";
import { LicenseMoreActionButton } from "../../components/infrastructure/LicenseMoreActionButton";
import { useGenericDrawer } from "melco-shared-logic";
import { LicenseActivationReminderDrawer } from "../../components/infrastructure/LicenseActivationReminderDrawer";
import { LicenseDetailForm } from "../../components/infrastructure/LicenseDetailForm";
import { LicensesHistoryList } from "../../components/infrastructure/LicensesHistoryList";
import { AssignUser } from "../../components/infrastructure/AssignUser";
import { AssignUserDrawer } from "../../components/infrastructure/AssignUserDrawer";
import { StatusColorMap } from "../../types/status";
import { FormattedUTCDate } from "../../components/date/FormattedUTCDate";
import { AsyncTooltip, TooltipBulletItem, TooltipContainer, TooltipText, TooltipTitle } from "../../components/tootltip/AsyncTooltip";
import { useSubscriptionGetPendingChanges } from "../../hooks/infrastructure/useSubscriptionGetPendingChanges";
import { useState } from "react";
import { formatUTCDate } from "../../helper/date/dateHelper";
import { capitalize } from "../../helper/common/util";
import styled from "styled-components";

const { spacings } = DesignTokens;

const CellWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
`;

type LicenseDetailsProps = {
  licenseId: string;
};

export const LicenseDetails: React.FC<LicenseDetailsProps> = ({
  licenseId,
}) => {
  const { t } = useTranslation();
  const breadcrumb = useBreadcrumb("infrastructure", "infrastructureLicenses");
  const activationReminderDrawer = useGenericDrawer();
  const assignUserDrawer = useGenericDrawer();

  const { data: license } = useLicense(licenseId);
  const {
    id,
    is_subscription,

    display_name,
    product_name,
    product_version_number,
    product_level_name,
    serial_number,
    activated_on,
    updated_date,
    product_code,
    status,
    number_of_assigned_machines,
    renewal_date,

    license_type,
    sub_cancelled_date } = license || {};

  const color = StatusColorMap[status ?? -1];
  const label = t(`infrastructureLicenses.licenseStatus.${status}`, '');


  const [activeTooltipId, setActiveTooltipId] = useState<string | null>(null);

  const handleToggle = (id: string | null) => {
    setActiveTooltipId((prev) => (prev === id ? null : id));
  };



  /**
   * Backend is sending UTC ,  
   * 
   * "sub_cancelled_date": "2025-10-10T00:00:00Z",
   *  Due to 00:00:00 we have to consider date explicitly as UTC 
   * because timezone with -hours like UTC -7 ,UTC -6 will show 
   * previous date
   */
  const renewal_or_expired_date = sub_cancelled_date ?
    <FormattedUTCDate date={sub_cancelled_date} />
    :
    <FormattedUTCDate date={renewal_date} />

  const renewal_or_expired_label = sub_cancelled_date ?
    t("infrastructureLicenses.form.label.expires_at")
    :
    t("infrastructureLicenses.form.label.renewal_date")

  return (
    <MainContentArea
      header={
        <ContentHeader
          title={
            <ValueOrFallback
              value={display_name}
              fallback={t("global.fallback.empty")}
            />
          }
          breadcrumb={breadcrumb}
          extra={
            <>
              <HeaderButtonArea />
              {license && (
                <LicenseMoreActionButton
                  activationReminderDrawer={activationReminderDrawer}
                />
              )}
            </>
          }
        />
      }
      contentPadding
    >
      <FullWidthSpace direction="vertical" size="large">
        <div>{license && <LicenseDetailForm license={license} />}</div>

        <div>
          <Title level={4}>
            {t("infrastructureLicenses.form.title.licenseDetails")}
          </Title>

          <Row gutter={[spacings.middle, 0]}>
            <Col xs={24} md={6}>
              <Space direction="vertical" size="middle">
                <LabeledItem
                  label={t("infrastructureLicenses.form.label.product_name")}
                  content={product_name ?? ""}
                  fallback={t("global.fallback.none")}
                />

                <LabeledItem
                  label={t(
                    "infrastructureLicenses.form.label.product_version_number"
                  )}
                  content={product_version_number ?? ""}
                  fallback={t("global.fallback.none")}
                />
                {/* <LabeledItem
                  label={t(
                    "infrastructureLicenses.form.label.license_type"
                  )}
                  //content={LicenseTypes[license_type ?? -1] ?? ""}
                  content={t(`infrastructureLicenses.licenseTypes.${license_type ?? -1}`, '')}
                  fallback={t("global.fallback.none")}
                /> */}

                <LabeledItem
                  label={t(
                    "infrastructureLicenses.form.label.product_level_name"
                  )}
                  content={product_level_name ?? ""}
                  fallback={t("global.fallback.none")}
                />
              </Space>
            </Col>
            <Col xs={24} md={6}>
              <Space direction="vertical" size="middle">
                <LabeledItem
                  label={t("infrastructureLicenses.form.label.serial_number")}
                  content={serial_number ?? ""}
                  fallback={t("global.fallback.none")}
                />

                <LabeledItem
                  label={t("infrastructureLicenses.form.label.activated_on")}
                  content={<FormattedDate date={activated_on} />}
                  fallback={t("global.fallback.none")}
                />

                <LabeledItem
                  label={t("infrastructureLicenses.form.label.updated_date")}
                  content={<FormattedDate date={updated_date} />}
                  fallback={t("global.fallback.none")}
                />
              </Space>
            </Col>

            <Col xs={24} md={6}>
              <Space direction="vertical" size="middle">
                <LabeledItem
                  label={t(
                    "infrastructureLicenses.form.label.status"
                  )}
                  content={<CellWrapper><Text type={color}>{label}</Text> {
                    status == 2 && (<AsyncTooltip
                      id={id ?? ""}
                      param={id ?? ""}
                      isActive={activeTooltipId === id}
                      onToggle={handleToggle}
                      useDataHook={useSubscriptionGetPendingChanges}
                      renderContent={(data) => {
                        if (!data) return <TooltipText>{t("infrastructureLicenses.tooltip.no_data_available")}</TooltipText>;

                        const date = formatUTCDate(data.sub_start_date, "YYYY-MM-DD");
                        const from = capitalize(data?.change_from ?? "");
                        const to = capitalize(data?.change_to ?? "");

                        return (
                          <TooltipContainer>
                            <TooltipTitle>{t("infrastructureLicenses.tooltip.changes_apply", { date })}</TooltipTitle>
                            <TooltipBulletItem>
                              {t("infrastructureLicenses.tooltip.billing_change", { from, to })}
                            </TooltipBulletItem>
                          </TooltipContainer>
                        );
                      }}
                    />
                    )

                  }</CellWrapper>}
                  fallback={t("global.fallback.none")}
                />

                {is_subscription && <LabeledItem
                  label={renewal_or_expired_label}
                  content={renewal_or_expired_date}
                  fallback={t("global.fallback.none")}
                />}


              </Space>
            </Col>

            <Col xs={24} md={6}>
              <Space direction="vertical" size="middle">
                {license && (
                  <AssignUser license={license} drawer={assignUserDrawer} />
                )}

                {is_subscription && <LabeledItem
                  label={t("infrastructureLicenses.form.label.number_of_assigned_machines")}
                  content={number_of_assigned_machines ?? ""}
                  fallback={t("global.fallback.none")}
                />}
              </Space>

            </Col>
          </Row>
        </div>

        <div>
          <Title level={4}>
            {t("infrastructureLicenses.form.title.history")}
          </Title>

          <LicensesHistoryList licenseId={licenseId} />
        </div>
      </FullWidthSpace>

      {
        license && (
          <LicenseActivationReminderDrawer
            license={license}
            drawer={activationReminderDrawer}
          />
        )
      }

      {
        license && (
          <AssignUserDrawer
            product_code={product_code ? product_code : undefined}
            license={license}
            drawer={assignUserDrawer}
          />
        )
      }
    </MainContentArea >
  );
};
